/**
 * Shopify CountryProvinceSelector — modernized.
 *
 * The legacy ep-shopify_common.js shipped four utility helpers
 * (Shopify.bind, addListener, setSelectorByValue, postLink) plus a
 * jQuery-free CountryProvinceSelector that the customer address form
 * instantiated inline. The four helpers had no first-party consumers
 * (verified via grep across theme/) so they're dropped here.
 *
 * The CountryProvinceSelector keeps its public API on
 * `window.Shopify.CountryProvinceSelector` for any third-party code
 * that might still use it, and self-binds via a
 * `[data-country-province-selector]` element pattern so the inline
 * `<script>new Shopify.CountryProvinceSelector(...)</script>` block in
 * address-form.liquid can go away. The data-attribute pattern survives
 * Turbo nav, server-rendered customer pages, and dynamic insertion via
 * customers.esm.js's address-edit toggle.
 *
 * initAll() finds the country and province selects by their `name`
 * attributes relative to the containing form — no fragile ID derivation
 * needed. The constructor still accepts ID strings for backward compat
 * with any external callers (e.g. `new Shopify.CountryProvinceSelector('id1','id2',{})`).
 *
 * Migrated from theme/js/plugins/ep-shopify_common.js (IIFE → ESM,
 * Phase 11). The separate `<script src="ep-shopify_common.min.js">` in
 * the page head is removed; this module is bundled into entry-default.
 *
 * Source path: theme/js/plugins/ep-shopify_common.esm.js
 * Built name : ./ep-shopify_common.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 * Updated: 2026-05-08 (Claude Opus 4.7) - Relative DOM queries in initAll(); constructor accepts elements or ID strings
 *
 * [ShopifyCommon]
 */

import { log } from './log.js';

const Shopify = window.Shopify = window.Shopify || {};

class CountryProvinceSelector {
  constructor(countryElOrId, provinceElOrId, options) {
    this.countryEl = typeof countryElOrId === 'string'
      ? document.getElementById(countryElOrId) : countryElOrId;
    this.provinceEl = typeof provinceElOrId === 'string'
      ? document.getElementById(provinceElOrId) : provinceElOrId;
    // If hideElement is provided (external / backward-compat callers), look it up by ID.
    // Otherwise infer the province field wrapper from the select's DOM position.
    const hideId = options && options.hideElement;
    this.provinceContainer = hideId
      ? document.getElementById(hideId)
      : (this.provinceEl ? this.provinceEl.closest('.field') : null);
    if (!this.countryEl || !this.provinceEl) {
      console.warn('[ShopifyCommon] CountryProvinceSelector: element not found', { countryElOrId, provinceElOrId });
      return;
    }

    this.countryEl.addEventListener('change', () => this.countryHandler());
    this.initCountry();
    this.initProvince();
  }

  initCountry() {
    const value = this.countryEl.getAttribute('data-default');
    setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  }

  initProvince() {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      setSelectorByValue(this.provinceEl, value);
    }
  }

  countryHandler() {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    if (!opt) return;
    const raw = opt.getAttribute('data-provinces');
    let provinces = [];
    try { provinces = JSON.parse(raw) || []; } catch (e) { provinces = []; }

    this.clearOptions(this.provinceEl);
    if (provinces.length === 0) {
      if (this.provinceContainer) this.provinceContainer.style.display = 'none';
    } else {
      this.setOptions(this.provinceEl, provinces);
      if (this.provinceContainer) this.provinceContainer.style.display = 'block';
    }
  }

  clearOptions(selector) {
    while (selector.firstChild) selector.removeChild(selector.firstChild);
    selector.dispatchEvent(new Event('change', { bubbles: false }));
  }

  setOptions(selector, values) {
    const placeholder = document.createElement('option');
    placeholder.selected = true;
    placeholder.disabled = true;
    placeholder.innerHTML = 'Select State';
    placeholder.setAttribute('data-extra', 'placeholder');
    selector.appendChild(placeholder);
    for (let i = 0; i < values.length; i++) {
      const opt = document.createElement('option');
      opt.value = values[i][0];
      opt.innerHTML = values[i][1];
      selector.appendChild(opt);
    }
    selector.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function setSelectorByValue(selector, value) {
  for (let i = 0; i < selector.options.length; i++) {
    if (value == selector.options[i].value) {
      selector.selectedIndex = i;
      return i;
    }
  }
}

Shopify.CountryProvinceSelector = CountryProvinceSelector;

const ROOT_SELECTOR = '[data-country-province-selector]';
const initializedAttr = 'data-country-province-selector-initialized';

function initAll() {
  document.querySelectorAll(ROOT_SELECTOR).forEach((wrap) => {
    if (wrap.hasAttribute(initializedAttr)) return;
    const form = wrap.closest('form');
    if (!form) return;
    const countryEl = form.querySelector('select[name="address[country]"]');
    const provinceEl = form.querySelector('select[name="address[province]"]');
    if (!countryEl || !provinceEl) return;
    new CountryProvinceSelector(countryEl, provinceEl);
    wrap.setAttribute(initializedAttr, '1');
  });
}

// Initial pass after parse.
initAll();

// customers.esm.js toggles the new-address / edit-address forms in/out
// of view at click time. The address forms are server-rendered (already
// in the DOM at parse), so initAll() at module eval picks them all up.
// Re-run on Turbo navigation between customer pages just in case.
document.addEventListener('ep:ready', initAll);
document.addEventListener('ep:change', initAll);

log('[ShopifyCommon] registered');
